import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://ivision-crm-backend-production.up.railway.app';

const BUSINESS_TYPES = [
  { value: 'rental', label: 'Equipment Rental', icon: '🏗️', desc: 'Heavy equipment, tools, party/event gear' },
  { value: 'auto_shop', label: 'Auto Shop', icon: '🔧', desc: 'Repair, detailing, tire shops' },
  { value: 'spa', label: 'Spa & Massage', icon: '💆', desc: 'Day spas, massage studios, wellness' },
  { value: 'barbershop', label: 'Barbershop', icon: '✂️', desc: 'Barbershops, salons, hair studios' },
  { value: 'smoke_shop', label: 'Smoke Shop', icon: '💨', desc: 'Smoke, vape, and tobacco retail' },
  { value: 'other', label: 'Other', icon: '🏢', desc: 'Local service business' },
];

export default function SignupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    business_name: '', business_type: '', phone: '', city: '', state: '', email: '', password: '', confirm_password: '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const step1Valid = form.business_name && form.business_type;
  const step2Valid = form.email && form.password && form.password === form.confirm_password && form.password.length >= 8;

  const handleSubmit = async () => {
    if (!step2Valid) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ business_name: form.business_name, business_type: form.business_type, phone: form.phone, city: form.city, state: form.state, email: form.email, password: form.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Signup failed');
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setStep(3);
      setTimeout(() => navigate('/dashboard'), 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&family=Space+Mono&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .sp-root { min-height: 100vh; background: #0d0d0d; display: flex; flex-direction: column; font-family: 'DM Sans', sans-serif; color: #f5f4f0; }
        .sp-topbar { display: flex; justify-content: space-between; align-items: center; padding: 16px 32px; border-bottom: 1px solid #1e1f23; background: #0d0d0d; flex-shrink: 0; }
        .sp-back-btn { color: #8a8880; font-size: 13px; font-weight: 500; text-decoration: none; transition: color 0.2s; }
        .sp-back-btn:hover { color: #f5a623; }
        .sp-login-btn { background: transparent; border: 1px solid #2a2b30; color: #f5f4f0; padding: 8px 20px; border-radius: 5px; font-size: 13px; font-weight: 600; text-decoration: none; transition: all 0.2s; }
        .sp-login-btn:hover { border-color: #f5a623; color: #f5a623; }
        .sp-body { display: flex; flex: 1; }
        .sp-left { width: 380px; flex-shrink: 0; background: #111214; border-right: 1px solid #1e1f23; display: flex; flex-direction: column; padding: 48px 40px; position: relative; overflow: hidden; }
        .sp-left::before { content: ''; position: absolute; bottom: -100px; left: -100px; width: 400px; height: 400px; background: radial-gradient(circle, rgba(245,166,35,0.07) 0%, transparent 70%); pointer-events: none; }
        .sp-logo { font-family: 'Bebas Neue', sans-serif; font-size: 26px; letter-spacing: 3px; color: #f5a623; margin-bottom: 48px; }
        .sp-logo span { color: #f5f4f0; }
        .sp-left-title { font-family: 'Bebas Neue', sans-serif; font-size: 44px; line-height: 1; letter-spacing: 2px; color: #f5f4f0; margin-bottom: 14px; }
        .sp-left-title em { font-style: normal; color: #f5a623; }
        .sp-left-sub { font-size: 14px; color: #8a8880; line-height: 1.6; margin-bottom: 40px; }
        .sp-steps { display: flex; flex-direction: column; }
        .sp-step-item { display: flex; align-items: flex-start; gap: 14px; padding: 14px 0; position: relative; }
        .sp-step-item:not(:last-child)::after { content: ''; position: absolute; left: 15px; top: 44px; width: 2px; height: calc(100% - 12px); background: #1e1f23; }
        .sp-step-item.active::after { background: rgba(245,166,35,0.3); }
        .sp-step-num { width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; flex-shrink: 0; border: 2px solid #1e1f23; color: #8a8880; background: #0d0d0d; font-family: 'Space Mono', monospace; transition: all 0.3s; position: relative; z-index: 1; }
        .sp-step-item.active .sp-step-num { border-color: #f5a623; color: #f5a623; background: rgba(245,166,35,0.1); }
        .sp-step-item.done .sp-step-num { border-color: #27c93f; color: #27c93f; background: rgba(39,201,63,0.1); }
        .sp-step-label { font-size: 13px; color: #8a8880; font-weight: 500; padding-top: 5px; }
        .sp-step-item.active .sp-step-label { color: #f5f4f0; }
        .sp-step-desc { font-size: 11px; color: #4a4a4f; margin-top: 2px; }
        .sp-right { flex: 1; display: flex; align-items: center; justify-content: center; padding: 48px 40px; }
        .sp-form-wrap { width: 100%; max-width: 480px; }
        .sp-form-title { font-family: 'Bebas Neue', sans-serif; font-size: 36px; letter-spacing: 2px; color: #f5f4f0; margin-bottom: 6px; }
        .sp-form-sub { font-size: 14px; color: #8a8880; margin-bottom: 32px; }
        .sp-type-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 24px; }
        .sp-type-card { padding: 14px; border: 2px solid #1e1f23; border-radius: 8px; cursor: pointer; transition: all 0.2s; background: #111214; }
        .sp-type-card:hover { border-color: rgba(245,166,35,0.4); }
        .sp-type-card.selected { border-color: #f5a623; background: rgba(245,166,35,0.06); }
        .sp-type-icon { font-size: 20px; margin-bottom: 6px; }
        .sp-type-label { font-size: 13px; font-weight: 600; color: #f5f4f0; margin-bottom: 2px; }
        .sp-type-desc { font-size: 11px; color: #8a8880; }
        .sp-field { margin-bottom: 16px; }
        .sp-label { font-size: 11px; color: #8a8880; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 7px; display: block; }
        .sp-input { width: 100%; background: #111214; border: 1px solid #2a2b30; border-radius: 7px; padding: 12px 16px; font-size: 14px; color: #f5f4f0; font-family: 'DM Sans', sans-serif; transition: border-color 0.2s; outline: none; }
        .sp-input:focus { border-color: #f5a623; }
        .sp-input::placeholder { color: #4a4a4f; }
        .sp-input-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .sp-pw-bar { height: 3px; border-radius: 2px; background: #1e1f23; margin-top: 8px; overflow: hidden; }
        .sp-pw-fill { height: 100%; border-radius: 2px; transition: all 0.3s; }
        .sp-error { background: rgba(255,95,86,0.1); border: 1px solid rgba(255,95,86,0.3); border-radius: 6px; padding: 12px 14px; font-size: 13px; color: #ff5f56; margin-bottom: 16px; }
        .sp-btn { width: 100%; padding: 14px; border-radius: 7px; font-size: 15px; font-weight: 700; cursor: pointer; border: none; font-family: 'DM Sans', sans-serif; transition: all 0.2s; display: flex; align-items: center; justify-content: center; gap: 8px; }
        .sp-btn-primary { background: #f5a623; color: #0d0d0d; }
        .sp-btn-primary:hover:not(:disabled) { background: #ffbe4d; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(245,166,35,0.3); }
        .sp-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .sp-btn-back { background: transparent; color: #8a8880; border: 1px solid #2a2b30; margin-bottom: 10px; }
        .sp-btn-back:hover { border-color: #f5a623; color: #f5a623; }
        .sp-link-row { text-align: center; margin-top: 18px; font-size: 13px; color: #8a8880; }
        .sp-link-row a { color: #f5a623; text-decoration: none; font-weight: 600; }
        .sp-success { text-align: center; padding: 40px 20px; }
        .sp-success-icon { width: 80px; height: 80px; border-radius: 50%; background: rgba(39,201,63,0.1); border: 2px solid #27c93f; display: flex; align-items: center; justify-content: center; font-size: 36px; margin: 0 auto 24px; animation: pop 0.4s cubic-bezier(0.175,0.885,0.32,1.275); }
        @keyframes pop { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .sp-success-title { font-family: 'Bebas Neue', sans-serif; font-size: 40px; letter-spacing: 2px; color: #f5f4f0; margin-bottom: 10px; }
        .sp-success-sub { font-size: 15px; color: #8a8880; margin-bottom: 8px; }
        .sp-success-redirect { font-size: 12px; color: #4a4a4f; font-family: 'Space Mono', monospace; }
        @media (max-width: 768px) { .sp-left { display: none; } .sp-right { padding: 32px 24px; } }
      `}</style>

      <div className="sp-root">
        <div className="sp-topbar">
          <Link to="/" className="sp-back-btn">← Back to Home</Link>
          <Link to="/login" className="sp-login-btn">Log In</Link>
        </div>

        <div className="sp-body">
          <div className="sp-left">
            <div className="sp-logo">IVISION <span>CRM</span></div>
            <div className="sp-left-title">Your Shop.<br /><em>Online.</em><br />In 24hrs.</div>
            <p className="sp-left-sub">Stop juggling spreadsheets and phone calls. IVision gives your business a full platform — booking, CRM, invoicing, and SMS — at a fraction of what the big guys charge.</p>
            <div className="sp-steps">
              {[
                { n: 1, label: 'Business Info', desc: 'Tell us about your shop' },
                { n: 2, label: 'Create Account', desc: 'Secure login credentials' },
                { n: 3, label: "You're Live", desc: 'Start using IVision instantly' },
              ].map(s => (
                <div key={s.n} className={`sp-step-item ${step === s.n ? 'active' : step > s.n ? 'done' : ''}`}>
                  <div className="sp-step-num">{step > s.n ? '✓' : s.n}</div>
                  <div>
                    <div className="sp-step-label">{s.label}</div>
                    <div className="sp-step-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="sp-right">
            <div className="sp-form-wrap">

              {step === 1 && (
                <>
                  <div className="sp-form-title">Tell Us About<br />Your Business</div>
                  <p className="sp-form-sub">We'll configure your platform for your industry automatically.</p>
                  <div style={{ marginBottom: '8px' }}>
                    <label className="sp-label">Business Type</label>
                    <div className="sp-type-grid">
                      {BUSINESS_TYPES.map(t => (
                        <div key={t.value} className={`sp-type-card${form.business_type === t.value ? ' selected' : ''}`} onClick={() => set('business_type', t.value)}>
                          <div className="sp-type-icon">{t.icon}</div>
                          <div className="sp-type-label">{t.label}</div>
                          <div className="sp-type-desc">{t.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="sp-field">
                    <label className="sp-label">Business Name</label>
                    <input className="sp-input" placeholder="e.g. Apex Equipment Rentals" value={form.business_name} onChange={e => set('business_name', e.target.value)} />
                  </div>
                  <div className="sp-input-row">
                    <div className="sp-field">
                      <label className="sp-label">City</label>
                      <input className="sp-input" placeholder="Las Vegas" value={form.city} onChange={e => set('city', e.target.value)} />
                    </div>
                    <div className="sp-field">
                      <label className="sp-label">State</label>
                      <input className="sp-input" placeholder="NV" value={form.state} onChange={e => set('state', e.target.value)} maxLength={2} />
                    </div>
                  </div>
                  <div className="sp-field">
                    <label className="sp-label">Phone (optional)</label>
                    <input className="sp-input" placeholder="(702) 555-0100" value={form.phone} onChange={e => set('phone', e.target.value)} />
                  </div>
                  <button className="sp-btn sp-btn-primary" disabled={!step1Valid} onClick={() => setStep(2)}>Continue →</button>
                  <p className="sp-link-row">Already have an account? <Link to="/login">Log in</Link></p>
                </>
              )}

              {step === 2 && (
                <>
                  <div className="sp-form-title">Create Your<br />Account</div>
                  <p className="sp-form-sub">Setting up <strong style={{ color: '#f5a623' }}>{form.business_name}</strong> on IVision.</p>
                  {error && <div className="sp-error">⚠ {error}</div>}
                  <div className="sp-field">
                    <label className="sp-label">Email Address</label>
                    <input className="sp-input" type="email" placeholder="you@yourbusiness.com" value={form.email} onChange={e => set('email', e.target.value)} />
                  </div>
                  <div className="sp-field">
                    <label className="sp-label">Password</label>
                    <input className="sp-input" type="password" placeholder="Minimum 8 characters" value={form.password} onChange={e => set('password', e.target.value)} />
                    {form.password && (
                      <div className="sp-pw-bar">
                        <div className="sp-pw-fill" style={{ width: `${Math.min(100, (form.password.length / 12) * 100)}%`, background: form.password.length < 8 ? '#ff5f56' : form.password.length < 12 ? '#f5a623' : '#27c93f' }} />
                      </div>
                    )}
                  </div>
                  <div className="sp-field">
                    <label className="sp-label">Confirm Password</label>
                    <input className="sp-input" type="password" placeholder="Re-enter password" value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} style={{ borderColor: form.confirm_password && form.confirm_password !== form.password ? '#ff5f56' : '' }} />
                    {form.confirm_password && form.confirm_password !== form.password && (
                      <div style={{ fontSize: '12px', color: '#ff5f56', marginTop: '6px' }}>Passwords don't match</div>
                    )}
                  </div>
                  <button className="sp-btn sp-btn-back" onClick={() => setStep(1)}>← Back</button>
                  <button className="sp-btn sp-btn-primary" disabled={!step2Valid || loading} onClick={handleSubmit}>
                    {loading ? 'Creating Account...' : 'Launch My Platform →'}
                  </button>
                  <p className="sp-link-row" style={{ marginTop: '16px' }}>
                    By signing up you agree to our <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.
                  </p>
                </>
              )}

              {step === 3 && (
                <div className="sp-success">
                  <div className="sp-success-icon">✓</div>
                  <div className="sp-success-title">You're Live.</div>
                  <p className="sp-success-sub"><strong style={{ color: '#f5a623' }}>{form.business_name}</strong> is ready on IVision.</p>
                  <p className="sp-success-sub">Redirecting you to your dashboard...</p>
                  <p className="sp-success-redirect">// loading your platform</p>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </>
  );
}
